import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import {
  LEGAL_CATEGORY_LABELS,
  LEGAL_POLICY_CATEGORY_KEYS,
  deleteLegalPolicyVersion,
  fetchLegalPolicyVersions,
  formatScopedVendorType,
  setLegalPolicyPublished,
  type LegalAudience,
  type LegalCategory,
  type LegalPolicyVersionDto,
} from '../../services/MasterAdmin/legalPolicyVersionsService';

type ConfirmTone = 'danger' | 'warning' | 'success';

interface ConfirmState {
  title: string;
  message: string;
  tone: ConfirmTone;
  confirmLabel: string;
  onConfirm: () => Promise<void> | void;
}

const getApiErrorMessage = (e: unknown, fallback: string): string => {
  if (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === 'string'
  ) {
    return String((e as { response?: { data?: { message?: string } } }).response?.data?.message);
  }
  return fallback;
};

const LegalPoliciesManagementPage: React.FC = () => {
  const [rows, setRows] = useState<LegalPolicyVersionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterAudience, setFilterAudience] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterPublished, setFilterPublished] = useState<string>('');
  const [viewRow, setViewRow] = useState<LegalPolicyVersionDto | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const publishFilterLabel = useMemo(() => {
    if (filterPublished === 'true') return 'Published only';
    if (filterPublished === 'false') return 'Drafts only';
    return 'Published + drafts';
  }, [filterPublished]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof fetchLegalPolicyVersions>[0] = {};
      if (filterAudience) params.audience = filterAudience as LegalAudience;
      if (filterCategory) params.category = filterCategory as LegalCategory;
      if (filterPublished === 'true' || filterPublished === 'false') {
        params.isPublished = filterPublished === 'true';
      }
      const response = await fetchLegalPolicyVersions(params);
      setRows(response.data || []);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Failed to load legal policies.'));
    } finally {
      setLoading(false);
    }
  }, [filterAudience, filterCategory, filterPublished]);

  useEffect(() => {
    void load();
  }, [load]);

  const closeMenu = useCallback(() => setOpenMenuId(null), []);

  const runConfirm = async () => {
    if (!confirm) return;
    try {
      setConfirmBusy(true);
      await confirm.onConfirm();
      setConfirm(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const askDelete = (row: LegalPolicyVersionDto) => {
    closeMenu();
    setConfirm({
      title: 'Delete this draft?',
      message: `“${LEGAL_CATEGORY_LABELS[row.category]} v${row.version}” will be permanently removed. This cannot be undone.`,
      tone: 'danger',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await deleteLegalPolicyVersion(row.id);
          await load();
        } catch (e: unknown) {
          setError(getApiErrorMessage(e, 'Delete failed.'));
        }
      },
    });
  };

  const askPublish = (row: LegalPolicyVersionDto) => {
    closeMenu();
    setConfirm({
      title: 'Publish this draft?',
      message: 'Apps and websites will start serving this version immediately.',
      tone: 'success',
      confirmLabel: 'Publish',
      onConfirm: async () => {
        try {
          await setLegalPolicyPublished(row.id, true);
          await load();
        } catch (e: unknown) {
          setError(getApiErrorMessage(e, 'Publish failed.'));
        }
      },
    });
  };

  const askUnpublish = (row: LegalPolicyVersionDto) => {
    closeMenu();
    setConfirm({
      title: 'Unpublish this version?',
      message: 'Apps will fall back to the previous published version for this stream (if any).',
      tone: 'warning',
      confirmLabel: 'Unpublish',
      onConfirm: async () => {
        try {
          await setLegalPolicyPublished(row.id, false);
          await load();
        } catch (e: unknown) {
          setError(getApiErrorMessage(e, 'Unpublish failed.'));
        }
      },
    });
  };

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto space-y-5 p-3 sm:p-4 md:p-6">
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Audience</span>
            <select
              value={filterAudience}
              onChange={(e) => setFilterAudience(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            >
              <option value="">All audiences</option>
              <option value="USER">User</option>
              <option value="VENDOR">Vendor</option>
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Category</span>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            >
              <option value="">All categories</option>
              {LEGAL_POLICY_CATEGORY_KEYS.map((c) => (
                <option key={c} value={c}>
                  {LEGAL_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</span>
            <select
              value={filterPublished}
              onChange={(e) => setFilterPublished(e.target.value)}
              aria-label={`Filter policies: ${publishFilterLabel}`}
              title={publishFilterLabel}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            >
              <option value="">Published + drafts</option>
              <option value="true">Published only</option>
              <option value="false">Drafts only</option>
            </select>
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Count</span>
            <div className="rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
              {loading ? 'Loading…' : `${rows.length.toLocaleString()} row${rows.length === 1 ? '' : 's'}`}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-transparent select-none">.</span>
            <Link
              to="/operations/legal-policies/new"
              className="inline-flex h-[38px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              New version
            </Link>
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-left dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Audience
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Category
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Scope
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Ver.
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                    {row.audience === 'USER' ? (
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                        User
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">
                        Vendor
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">
                    {LEGAL_CATEGORY_LABELS[row.category]}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {row.audience === 'VENDOR' ? formatScopedVendorType(row.scopedVendorType) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-800 dark:text-gray-200">v{row.version}</td>
                  <td className="px-4 py-3 text-sm">
                    {row.isPublished ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-200">
                        <CheckCircle2 className="h-3 w-3" />
                        Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                        Draft
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowActionsMenu
                      row={row}
                      open={openMenuId === row.id}
                      onToggle={() => setOpenMenuId((prev) => (prev === row.id ? null : row.id))}
                      onClose={closeMenu}
                      onView={() => {
                        closeMenu();
                        setViewRow(row);
                      }}
                      onPublish={() => askPublish(row)}
                      onUnpublish={() => askUnpublish(row)}
                      onDelete={() => askDelete(row)}
                    />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                    No policy versions yet.{' '}
                    <Link className="text-blue-600 hover:underline dark:text-blue-400" to="/operations/legal-policies/new">
                      Create one
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {loading && rows.length === 0 && (
        <div className="flex justify-center py-12 text-gray-500 dark:text-gray-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      )}

      {viewRow && <ViewPolicyModal row={viewRow} onClose={() => setViewRow(null)} />}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          tone={confirm.tone}
          confirmLabel={confirm.confirmLabel}
          busy={confirmBusy}
          onCancel={() => (confirmBusy ? null : setConfirm(null))}
          onConfirm={() => void runConfirm()}
        />
      )}
    </div>
  );
};

interface RowActionsMenuProps {
  row: LegalPolicyVersionDto;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onView: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
}

const MENU_WIDTH = 208; // w-52
const VIEWPORT_MARGIN = 8;

const RowActionsMenu: React.FC<RowActionsMenuProps> = ({
  row,
  open,
  onToggle,
  onClose,
  onView,
  onPublish,
  onUnpublish,
  onDelete,
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const recomputePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 240;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    let top = rect.bottom + 4;
    if (top + menuHeight > viewportH - VIEWPORT_MARGIN) {
      const above = rect.top - menuHeight - 4;
      top = above >= VIEWPORT_MARGIN ? above : Math.max(VIEWPORT_MARGIN, viewportH - menuHeight - VIEWPORT_MARGIN);
    }
    let left = rect.right - MENU_WIDTH;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (left + MENU_WIDTH > viewportW - VIEWPORT_MARGIN) left = viewportW - MENU_WIDTH - VIEWPORT_MARGIN;

    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) recomputePosition();
    else setPos(null);
  }, [open, recomputePosition]);

  useEffect(() => {
    if (!open) return;
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      onClose();
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleViewportChange = () => recomputePosition();
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleEsc);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleEsc);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, onClose, recomputePosition]);

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width: MENU_WIDTH,
              visibility: pos ? 'visible' : 'hidden',
            }}
            className="z-[60] origin-top-right overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-lg ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-900"
          >
            <MenuItem
              icon={<Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
              onClick={onView}
              label="View"
            />
            <MenuLinkItem
              icon={<Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />}
              to={`/operations/legal-policies/${encodeURIComponent(row.id)}/acceptances`}
              label="Acceptances"
              onClick={onClose}
            />
            {!row.isPublished && (
              <MenuLinkItem
                icon={<Pencil className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
                to={`/operations/legal-policies/${encodeURIComponent(row.id)}/edit`}
                label="Edit"
                onClick={onClose}
              />
            )}
            {!row.isPublished && (
              <MenuItem
                icon={<CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />}
                onClick={onPublish}
                label="Publish"
              />
            )}
            {row.isPublished && (
              <MenuItem
                icon={<EyeOff className="h-4 w-4 text-orange-600 dark:text-orange-400" />}
                onClick={onUnpublish}
                label="Unpublish"
              />
            )}
            {!row.isPublished && (
              <>
                <div className="my-1 border-t border-gray-100 dark:border-gray-800" />
                <MenuItem
                  icon={<Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />}
                  onClick={onDelete}
                  label="Delete"
                  tone="danger"
                />
              </>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

interface MenuItemBaseProps {
  icon: React.ReactNode;
  label: string;
  tone?: 'default' | 'danger';
}

const baseMenuItemClass = (tone: 'default' | 'danger' = 'default') =>
  `flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
    tone === 'danger'
      ? 'text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/20'
      : 'text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800'
  }`;

const MenuItem: React.FC<MenuItemBaseProps & { onClick: () => void }> = ({ icon, label, onClick, tone }) => (
  <button type="button" role="menuitem" onClick={onClick} className={baseMenuItemClass(tone)}>
    {icon}
    <span className="font-medium">{label}</span>
  </button>
);

const MenuLinkItem: React.FC<MenuItemBaseProps & { to: string; onClick: () => void }> = ({
  icon,
  label,
  to,
  onClick,
  tone,
}) => (
  <Link to={to} role="menuitem" onClick={onClick} className={baseMenuItemClass(tone)}>
    {icon}
    <span className="font-medium">{label}</span>
  </Link>
);

interface ConfirmDialogProps {
  title: string;
  message: string;
  tone: ConfirmTone;
  confirmLabel: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  tone,
  confirmLabel,
  busy,
  onCancel,
  onConfirm,
}) => {
  const toneStyles: Record<ConfirmTone, { icon: React.ReactNode; bg: string; btn: string }> = {
    danger: {
      icon: <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />,
      bg: 'bg-red-100 dark:bg-red-900/30',
      btn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    },
    warning: {
      icon: <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />,
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      btn: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
    },
    success: {
      icon: <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />,
      bg: 'bg-green-100 dark:bg-green-900/30',
      btn: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
    },
  };
  const t = toneStyles[tone];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-start gap-3 p-5">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${t.bg}`}>{t.icon}</div>
          <div className="min-w-0 flex-1">
            <h3 id="confirm-title" className="text-base font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{message}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3 dark:border-gray-800 dark:bg-gray-900/40">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 ${t.btn}`}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const ViewPolicyModal: React.FC<{ row: LegalPolicyVersionDto; onClose: () => void }> = ({ row, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {LEGAL_CATEGORY_LABELS[row.category]} · v{row.version} · {row.audience}
            </div>
            {row.title && <div className="text-xs text-gray-500 dark:text-gray-400">{row.title}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Close
          </button>
        </div>
        <div className="max-w-none overflow-y-auto px-4 py-4 text-sm leading-relaxed text-gray-900 dark:text-gray-100 [&_a]:break-words [&_a]:text-blue-600 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-px [&_code]:font-mono [&_code]:text-xs dark:[&_code]:bg-gray-800 [&_h1]:mb-3 [&_h1]:text-lg [&_h1]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_li]:my-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-3 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:dark:bg-gray-800 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_td]:border [&_th]:border-gray-300 [&_td]:border-gray-300 [&_th]:p-2 [&_td]:p-2 [&_tr]:dark:border-gray-600 dark:[&_th]:border-gray-600 dark:[&_td]:border-gray-600 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-6">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{row.markdownContent}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default LegalPoliciesManagementPage;
